import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";

type ModifierType = "info" | "success" | "warning" | "error";
type Notification = {
  id: string;
  text: string;
  modifier: ModifierType;
  duration: number; // Duration in milliseconds
};

type NotificationContextType = {
  notify: (text: string, modifier: ModifierType, duration: number) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((currentNotifications) =>
      currentNotifications.filter((notification) => notification.id !== id)
    );
  }, []);

  const notify = useCallback(
    (text: string, modifier: ModifierType, duration: number) => {
      const id = Math.random().toString(36).substring(2, 15); // Simple unique ID
      setNotifications((currentNotifications) => [
        ...currentNotifications,
        { id, text, modifier, duration },
      ]);

      setTimeout(() => removeNotification(id), duration);
    },
    [removeNotification]
  );
  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <div className="toast toast-bottom toast-end">
        {notifications.map((notification) => {
          let modifierClass = `alert alert-${notification.modifier}`;
          return (
            <div role="alert" key={notification.id} className={modifierClass}>
              <span>{notification.text}</span>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
